import classNames from 'classnames';
import { IconCheck } from 'featherico';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import { createElement, useCallback, useEffect, useState } from 'react';
import type SparkMetaDto from '../../../../types/SparkMetaDto';
import { Status } from '../../../../types/SparkMetaDto';
import type { CurrentUserDto } from '../../../../types/UserDtos';
import { useAppContext } from '../../hooks/useAppContext';
import { useIsVisible } from '../../hooks/useIsVisible';
import Button from '../Button';
import { DeprecatedButtonContainer } from '../ButtonContainer';
import Modal from '../Modal';
import { MultiModal, MultiModalPage } from '../MultiModal';
import RichText from '../RichText';
import UiPopover from '../UiPopover';
import type {
    Spark as ISpark,
    InlineSpark,
    ModalSpark,
    MultiModalSpark,
    PopoverSpark,
    TypeAndPayload,
} from './content';
import SPARK_CONTENT, { BodyType, ComponentType } from './content';
import * as service from './service';
import styles from './style.m.less';

// These flags can be used for debugging (always show sparks)
// and as failsafe to disable them altogether
const DISABLED = false;

function isActiveSpark(spark: ISpark, status: Status | undefined, user: CurrentUserDto) {
    // Always show sparks to small improvements users, otherwise we tend to forget about them.
    // (Exception: autorun-sparks, because that would be too annoying. They should be rare anyway.)
    return (user.isSmallImprovements && !spark.autorun) || Status.ACTIVE === status;
}

interface SparkProps {
    story: string;
    type: string;
    description?: string;
    direction?: string;
    onVisit?: (meta: SparkMetaDto) => void;
}

export default function Spark({ story, type, description, direction, onVisit }: SparkProps) {
    const spark = (SPARK_CONTENT[story] || {})[type];
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState<Status | undefined>();
    const appContext = useAppContext();

    useEffect(() => {
        if (!spark || DISABLED) {
            return;
        }

        if (!service.isSparkInTimeRange(spark, Date.now())) {
            console.warn(`Spark '${story} ${type}' is outside of configured time range`); // eslint-disable-line no-console
            return;
        }

        if (spark.persistent) {
            setOpen(!!spark.autorun);
            setStatus(Status.ACTIVE);
        } else {
            service.visit(story, type, spark.visibility).then(meta => {
                onVisit?.(meta);
                setOpen(!!spark.autorun);
                setStatus(meta.status);
            });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const handleDismiss = useCallback(() => {
        if (!spark.persistent) {
            service.dismiss(story, type);
        }
        setOpen(false);
        setStatus(Status.INACTIVE);
    }, [story, type]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    if (DISABLED || !spark || !isActiveSpark(spark, status, appContext.user)) {
        return null;
    }

    if (spark.componentType === ComponentType.INLINE && spark.containerless && spark.autorun) {
        if (!open) {
            return null;
        }
        return (
            <RevealedSpark
                spark={spark}
                direction={direction}
                onClose={handleDismiss}
                onDismiss={handleDismiss}
                label={description}
            />
        );
    }

    return (
        <div className={classNames(styles.spark_container, 'no-print')}>
            {spark.autorun ? null : <GlowingSpark onOpen={handleOpen} description={description} />}
            {open && (
                <RevealedSpark
                    spark={spark}
                    direction={direction}
                    onClose={spark.autorun ? handleDismiss : handleClose}
                    onDismiss={handleDismiss}
                    label={description}
                />
            )}
        </div>
    );
}

Spark.proptypes = {
    type: PropTypes.string.isRequired,
    story: PropTypes.string.isRequired,
    description: PropTypes.string,
    direction: PropTypes.string,
};

interface GlowingSparkProps {
    onOpen: () => void;
    description?: string;
}

function GlowingSpark({ onOpen, description }: GlowingSparkProps) {
    const { isVisible, ref } = useIsVisible<HTMLButtonElement>();

    const buttonClasses = classNames({
        [styles.spark]: true,
        [styles.sparkVisible]: isVisible,
        [styles.sparkWithDescription]: description,
    });

    const outerGlowClasses = classNames({
        [styles.glowing_outer]: true,
        [styles.glowingWithDescription_outer]: description,
    });

    const innerGlowClasses = classNames({
        [styles.glowing_inner]: true,
        [styles.glowingWithDescription_inner]: description,
    });

    return (
        <button ref={ref} className={buttonClasses} type="button" onClick={onOpen}>
            <div className={outerGlowClasses} />
            <div className={innerGlowClasses} />
            <div className={styles.description}>{description}</div>
        </button>
    );
}

interface RevealedSparkProps {
    spark: ISpark;
    direction?: string;
    onClose: () => void;
    onDismiss: () => void;
    label?: string;
}

function RevealedSpark(props: RevealedSparkProps) {
    switch (props.spark.componentType) {
        case ComponentType.POPOVER:
            return (
                <RevealedPopoverSpark
                    spark={props.spark}
                    direction={props.direction}
                    onClose={props.onClose}
                    onDismiss={props.onDismiss}
                />
            );
        case ComponentType.MULTI_MODAL:
            return <RevealedMultiModalSpark spark={props.spark} onDismiss={props.onDismiss} onClose={props.onClose} />;
        case ComponentType.MODAL:
            return <RevealedModalSpark spark={props.spark} onDismiss={props.onDismiss} onClose={props.onClose} />;
        case ComponentType.INLINE:
            return (
                <RevealedInlineSpark
                    spark={props.spark}
                    onDismiss={props.onDismiss}
                    onClose={props.onClose}
                    label={props.label}
                />
            );
        default:
            return null;
    }
}

interface RevealedMultiModalSparkProps {
    spark: MultiModalSpark;
    onClose: () => void;
    onDismiss: () => void;
}

function RevealedMultiModalSpark({ spark, onClose, onDismiss }: RevealedMultiModalSparkProps) {
    const { title, body, modalWidth, modalHeight } = spark;
    const { backgroundImage, steps } = body;

    const props = {
        isOpen: true,
        title,
        onClose: onClose,
        onDone: onDismiss,
        doneLabel: <GotIt />,
        height: modalHeight || 300,
        size: modalWidth,
        backgroundImage,
        backgroundSize: 'cover',
    };
    return (
        <MultiModal {...props}>
            {map(steps, (step, i) => (
                <MultiModalPage key={i}>{renderPayload(step)}</MultiModalPage>
            ))}
        </MultiModal>
    );
}

interface RevealedModalSparkProps {
    spark: ModalSpark;
    onClose: () => void;
    onDismiss: () => void;
}

function RevealedModalSpark({ spark: { title, body }, onClose, onDismiss }: RevealedModalSparkProps) {
    const { backgroundImage, content } = body;

    const modalProps = {
        isOpen: true,
        title,
        onClose,
        width: 600,
        height: 300,
        backgroundImage,
    };
    return (
        <Modal {...modalProps}>
            {renderPayload(content)}
            <DeprecatedButtonContainer>
                <Button kind="PRIMARY" onClick={onDismiss} autoFocus testId="close">
                    <GotIt />
                </Button>
            </DeprecatedButtonContainer>
        </Modal>
    );
}

interface RevealedPopOverSparkProps {
    spark: PopoverSpark;

    direction?: string;
    onClose: () => void;
    onDismiss: () => void;
}

function RevealedPopoverSpark({ spark, direction = 'bottom--left', onClose, onDismiss }: RevealedPopOverSparkProps) {
    return (
        <UiPopover direction={direction} onFocusLost={onClose}>
            <div className={styles.content}>
                {spark.title && (
                    <div>
                        <h3 className={styles.title}>{spark.title}</h3>
                    </div>
                )}
                <div>{renderPayload(spark.body)}</div>
                <DeprecatedButtonContainer>
                    <Button kind="PRIMARY" isSmall={true} onClick={onDismiss} autoFocus testId="close">
                        <GotIt />
                    </Button>
                </DeprecatedButtonContainer>
            </div>
        </UiPopover>
    );
}

function RevealedInlineSpark(props: {
    spark: InlineSpark;
    onDismiss: () => void;
    onClose?: () => void;
    label?: string;
}) {
    const {
        spark: { body: SparkComponent },
        onDismiss,
        onClose,
        label,
    } = props;
    return <SparkComponent onDismiss={onDismiss} onClose={onClose} label={label} />;
}

function GotIt() {
    return (
        <span>
            Got it <IconCheck />
        </span>
    );
}

function renderPayload(items: TypeAndPayload[]) {
    return map(items, (item, key) => (
        <div key={key} className={styles.body}>
            <Body item={item} />
        </div>
    ));
}

function Body({ item }: { item: TypeAndPayload }) {
    switch (item.type) {
        case BodyType.TEXT:
            return <RichText value={item.payload.text} sanitize={false} className={styles.text} />;
        case BodyType.IMAGE:
            return (
                <div>
                    <img src={item.payload.url} alt="" />
                </div>
            );
        case BodyType.VIDEO:
            return (
                <div>
                    <iframe src={item.payload.url} allowFullScreen />
                </div>
            );
        case BodyType.COMPONENT:
            return createElement(item.payload.component);
        default:
            return null;
    }
}
